/**
 * A function to create a log object for the 'agentRuntimeBenchmarkEvaluated' event (evid: 1)
 *
 * Event description: AI benchmark test run for Agent Maker
 *
 * @param {agentRuntimeBenchmarkEvaluatedParams} params - The parameters for this event
 * @property {string} automationId - automation guid
 * @property {string} benchmarkCategory - "response format" / "no harmful content" / etc.
 * @property {string} benchmarkTestName - "returns json" / "returns fields according to schema" / "swearwords eliminated" / etc.
 * @property {string} error - error string
 * @property {string} runtimeAgentResponse - stringified json response
 * @property {string} assessmentType - BINARY / RATING / tbd.
 * @property {number} assessmentValueNumeric - assessment value when type is RATING
 * @property {boolean} assessmentValueBool - assessment value when type is BOOL
 * @return {LogObject<agentRuntimeBenchmarkEvaluatedParams>} LogObject<agentRuntimeBenchmarkEvaluatedParams>
 */
export function agentRuntimeBenchmarkEvaluated(params) {
    return { evid: 1, src: 180, endpoint: '', params };
}
/**
 * A function to create a log object for the 'modelTriggerSrc180Evid10' event (evid: 10)
 *
 * Event description: User receives a modal after fulfilling it's condition
 *
 *
 * @param {modelTriggerSrc180Evid10Params} params - The parameters for this event
 * @property {string} modal_name - name of the modal
 * @property {string} origin - location of the modal
 * @property {GUID} task_id - task_id if relevant
 * @return {LogObject<modelTriggerSrc180Evid10Params>} LogObject<modelTriggerSrc180Evid10Params>
 */
export function modelTriggerSrc180Evid10(params) {
    return { evid: 10, src: 180, endpoint: '', params };
}
/**
 * A function to create a log object for the 'promptConverterSrc180Evid11' event (evid: 11)
 *
 * Event description: InstructionToPromptConverter prompt output
 *
 * @param {promptConverterSrc180Evid11Params} params - The parameters for this event
 * @property {string} outputTaskName - generated task name
 * @property {string} outputTaskInstructions - generated task instructions
 * @property {string} userInput - prompt input
 * @return {LogObject<promptConverterSrc180Evid11Params>} LogObject<promptConverterSrc180Evid11Params>
 */
export function promptConverterSrc180Evid11(params) {
    return { evid: 11, src: 180, endpoint: '', params };
}
/**
 * A function to create a log object for the 'taskCrudSrc180Evid12' event (evid: 12)
 *
 * Event description: Task CRUD (extra information for domain event)
 *
 * @param {taskCrudSrc180Evid12Params} params - The parameters for this event
 * @property {string} task_id - Task ID
 * @property {string} action - CRUD action - create, update, delete
 * @property {string} ai_used - was AI used to create a task?
 * @property {string} agent_id - agent id
 * @return {LogObject<taskCrudSrc180Evid12Params>} LogObject<taskCrudSrc180Evid12Params>
 */
export function taskCrudSrc180Evid12(params) {
    return { evid: 12, src: 180, endpoint: '', params };
}
/**
 * A function to create a log object for the 'aiBenchmarkTestStartedSrc180Evid5' event (evid: 5)
 *
 * Event description: For testing AI agent performance
 *
 * @param {aiBenchmarkTestStartedSrc180Evid5Params} params - The parameters for this event
 * @property {GUID} testExecutionId - uuid
 * @property {string} testName - name of test
 * @property {boolean} executedInCi - true/false
 * @return {LogObject<aiBenchmarkTestStartedSrc180Evid5Params>} LogObject<aiBenchmarkTestStartedSrc180Evid5Params>
 */
export function aiBenchmarkTestStartedSrc180Evid5(params) {
    return { evid: 5, src: 180, endpoint: '', params };
}
/**
 * A function to create a log object for the 'aiBenchmarkTestCompletedSrc180Evid6' event (evid: 6)
 *
 * Event description: For testing AI generation agent performance
 *
 * @param {aiBenchmarkTestCompletedSrc180Evid6Params} params - The parameters for this event
 * @property {GUID} testExecutionId - uuid
 * @property {string} testName - name of test
 * @property {boolean} executedInCi - true/false
 * @property {string} llmResponse - llm response
 * @property {number} toolCount - tool count
 * @property {number} executionTime - execution time in seconds
 * @property {string} microcentsSpent - microcents spent
 * @return {LogObject<aiBenchmarkTestCompletedSrc180Evid6Params>} LogObject<aiBenchmarkTestCompletedSrc180Evid6Params>
 */
export function aiBenchmarkTestCompletedSrc180Evid6(params) {
    return { evid: 6, src: 180, endpoint: '', params };
}
/**
 * A function to create a log object for the 'aiBenchmarkTestFailedSrc180Evid7' event (evid: 7)
 *
 * Event description: For testing AI agent performance
 *
 * @param {aiBenchmarkTestFailedSrc180Evid7Params} params - The parameters for this event
 * @property {GUID} testExecutionId - uuid
 * @property {string} testName - name of test
 * @property {boolean} executedInCi - true/false
 * @property {string} llmResponse - llm response
 * @property {string} error - error
 * @return {LogObject<aiBenchmarkTestFailedSrc180Evid7Params>} LogObject<aiBenchmarkTestFailedSrc180Evid7Params>
 */
export function aiBenchmarkTestFailedSrc180Evid7(params) {
    return { evid: 7, src: 180, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dashboardNavigationSrc180Evid8' event (evid: 8)
 *
 * Event description: User clicks one of the available buttons inside an AI Agent dashboard that will navigate them to a different route
 *
 *
 *
 *
 *
 * @param {dashboardNavigationSrc180Evid8Params} params - The parameters for this event
 * @property {string} additional_info - additional_ingo
 * @property {string} entity_id - relevant entity_id
 * @property {string} entity_type - relevant entity type: task, etc.
 * @property {string} origin - the button clicked that incentivize the navigation
 * @property {string} source_page - the page from which the user is navigating
 * @property {string} target_page - the page to which the user is navigating
 * @property {GUID} task_id - task_id if relevant
 * @return {LogObject<dashboardNavigationSrc180Evid8Params>} LogObject<dashboardNavigationSrc180Evid8Params>
 */
export function dashboardNavigationSrc180Evid8(params) {
    return { evid: 8, src: 180, endpoint: '', params };
}
/**
 * A function to create a log object for the 'actionClickSrc180Evid9' event (evid: 9)
 *
 * Event description: User clicks one of the available actions/buttons in an AI Agent dashboard
 *
 *
 *
 * @param {actionClickSrc180Evid9Params} params - The parameters for this event
 * @property {string} entity_id - relevant entity_id
 * @property {string} entity_type - relevant entity_type: task
 * @property {string} origin - button clicked
 * @property {string} source_page - route in which the click is taking place
 * @property {string} value - value of the click
 * @property {GUID} task_id - task_id if relevant
 * @return {LogObject<actionClickSrc180Evid9Params>} LogObject<actionClickSrc180Evid9Params>
 */
export function actionClickSrc180Evid9(params) {
    return { evid: 9, src: 180, endpoint: '', params };
}
//# sourceMappingURL=index.js.map